/**
 * Client side of the find-alternates fanout.
 *
 * Two TanStack hooks:
 * - `useFindAlternates(orgSlug)`: a mutation that POSTs an MPN and
 *   returns one row per org-enabled distributor. Modal renders this.
 * - `useAttachAlternate(orgSlug)`: the "Add as supplier" mutation
 *   that re-fetches the picked alternate via the lookup adapter and
 *   attaches it as a PartSupplier on the user's existing part.
 *
 * The fanout response is rendered verbatim — server orders rows by
 * the org's distributor priority, and the modal doesn't re-sort.
 * `additional[slug]` carries surplus matches the modal hides behind
 * a "Show N more" expander.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from './api';

export type AlternateStatus = 'found' | 'not_found' | 'error' | 'skipped';

export interface AlternateMatch {
  distributor_slug: string;
  distributor_name: string;
  status: AlternateStatus;
  distributor_pn: string | null;
  manufacturer: string | null;
  /** Vendor's reported MPN — may differ from `searched_mpn` when the
   * distributor returns a substitute for a discontinued part. */
  mpn: string | null;
  /** Echoed back from the request for client-side comparison. */
  searched_mpn: string | null;
  /** True when `mpn` differs from `searched_mpn` (case-fold compare).
   * Drives the amber "substitute" pill in the modal — the user is
   * looking at a different MPN than what they searched for. */
  is_substitute: boolean;
  /** True when the distributor's match is flagged obsolete in their
   * catalog. Found-but-obsolete; distinct from `not_found`. Modal
   * renders an "obsolete" pill when true. */
  is_obsolete: boolean;
  description: string | null;
  package: string | null;
  in_stock: boolean;
  stock_qty: number | null;
  price_unit_cents: number | null;
  currency: string | null;
  supplier_url: string | null;
  error_message: string | null;
}

export interface FindAlternatesResponse {
  mpn: string;
  results: AlternateMatch[];
  additional: Record<string, AlternateMatch[]>;
}

export interface FindAlternatesRequest {
  mpn: string;
  manufacturer?: string;
  exclude_distributor_slugs?: string[];
  limit_per_distributor?: number;
}

export interface AttachAlternateRequest {
  distributor_slug: string;
  distributor_pn: string;
  target_part_id: number;
  /** Phase 4 explicit-choice flags. All default to false on the
   * server, preserving "append at bottom" behavior for clients that
   * don't pass them. */
  make_primary?: boolean;
  /** Substitute confirmation path: when true, marks the previous
   * primary EOL with reason="superseded_by_substitute" and updates
   * Part.mfg_pn to the new vendor's MPN. Requires make_primary AND
   * prev_primary_supplier_id. */
  update_part_mpn?: boolean;
  /** Server verifies this matches the current priority-1 supplier,
   * 409s if not — guards against a "user promoted between fanout
   * and confirm" race that would otherwise silently mark the wrong
   * row EOL. Required when update_part_mpn is true. */
  prev_primary_supplier_id?: number;
}

export interface AttachAlternateResponse {
  id: number;
  distributor_id: number;
  distributor_slug: string;
  distributor_name: string;
  distributor_pn: string;
  supplier_url: string | null;
  priority: number;
  price_unit_cents: number | null;
  currency: string;
  moq: number | null;
}

export function useFindAlternates(orgSlug: string | undefined) {
  return useMutation<FindAlternatesResponse, Error, FindAlternatesRequest>({
    mutationFn: async (payload) => {
      if (!orgSlug) {
        throw new Error('No org selected');
      }
      return api.postJson<FindAlternatesResponse>(
        `/api/orgs/${orgSlug}/distributors/find-alternates`,
        payload,
      );
    },
  });
}

export interface FindSimilarRequest {
  description: string;
  exclude_distributor_slugs?: string[];
  limit_per_distributor?: number;
}

/**
 * Free-text replacement-finder. Same response shape as the MPN-fanout
 * so the modal can render both modes with one set of components.
 * The frontend treats the row's `searched_mpn` field as "what we
 * searched for" rather than literally an MPN — for similar-mode
 * results, it carries the description string verbatim.
 */
export function useFindSimilar(orgSlug: string | undefined) {
  return useMutation<FindAlternatesResponse, Error, FindSimilarRequest>({
    mutationFn: async (payload) => {
      if (!orgSlug) {
        throw new Error('No org selected');
      }
      return api.postJson<FindAlternatesResponse>(
        `/api/orgs/${orgSlug}/distributors/find-similar`,
        payload,
      );
    },
  });
}

export function useAttachAlternate(orgSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation<AttachAlternateResponse, Error, AttachAlternateRequest>({
    mutationFn: async (payload) => {
      if (!orgSlug) {
        throw new Error('No org selected');
      }
      return api.postJson<AttachAlternateResponse>(
        `/api/orgs/${orgSlug}/distributors/attach-to-part`,
        payload,
      );
    },
    onSuccess: (_data, variables) => {
      // Refresh the drawer's supplier list so the new row appears
      // without a manual reload. The drawer's TanStack key is
      // ['suppliers', orgSlug, partId] — invalidate the slug-scoped
      // family so any open drawer refetches.
      qc.invalidateQueries({ queryKey: ['suppliers', orgSlug] });
      qc.invalidateQueries({ queryKey: ['part', orgSlug, variables.target_part_id] });
    },
  });
}
